import * as Y from 'yjs'
import {
  graphicVariableOverrideKey,
  type Action,
  type GraphicVariableOverrideRecord,
  type MotionGraphicMetadata,
  type SubjectCanvasNode,
  type SetSubjectPlacement,
  type SubjectCollection,
  type WorkflowToolBundle,
} from '@/types/shared'

/**
 * Field-aware ID remapping for the project deep-copy primitive (spec/46).
 *
 * When a project is deep-copied (Duplicate, or snapshot restore as a new
 * project), the backend mints NEW Postgres rows for every set/character and
 * a NEW canvas asset id for each set node (Option R — see the spec for why we
 * re-key asset ids rather than changing the global UNIQUE constraint). This
 * module rewrites every reference to those ids inside the captured Yjs document
 * so the copy points exclusively at its OWN rows — no shared references back to
 * the source.
 *
 * IMPORTANT — what is NOT remapped:
 * - Non-set asset ids (they keep their ids; R2 blobs are shared and immutable).
 * - R2 URLs (they embed the old set/char ids in their paths and MUST stay
 *   byte-identical — same-environment copies share R2 per the never-delete policy).
 * - Pose ids, collection ids, stack ids, scene ids, clip ids, bin ids, variable
 *   ids, annotation ids (none get new Postgres rows; their uuids are preserved).
 *
 * A flat/substring string replace is explicitly avoided: composite keys
 * (`${setId}:${subjectId}`, `${setId}:${userId}:${mode}`) are decomposed and each
 * segment is remapped independently.
 */
export interface DeepCopyIdMaps {
  /** old set-node `asset_id` -> new `asset_id`. Contains ONLY set-linked canvas assets. */
  assetIdMap: Record<string, string>
  /**
   * The reworked lineage ledger's placements for the copy (ADR-0021), when the
   * ledger is on: the event every placed node carries as `lineageEvent`, and
   * which nodes (post-remap ids) were placed. Absent while the ledger is off.
   */
  lineage?: { event: string; placedNodeIds: string[] }
  /** old `sets.id` -> new `sets.id`. */
  setIdMap: Record<string, string>
  /**
   * old `characters.id` -> new `characters.id`. For character-category subjects
   * the subject-node id IS the character id, so this also remaps subject nodes,
   * placement `subject_node_id`, and collection membership.
   */
  charIdMap: Record<string, string>
  /** Source workflow id -> immutable tools frozen with the project copy or snapshot. */
  workflowToolBundles?: Record<string, WorkflowToolBundle>
  /**
   * Graphic asset id -> the metadata the copy carries, its private files copied under the new
   * project by the server. Absent from older servers.
   */
  motionGraphics?: Record<string, MotionGraphicMetadata>
}

const lookup = (map: Record<string, string>, id: string): string => map[id] ?? id

// ---------------------------------------------------------------------------
// Key transforms (pure — unit tested directly)
// ---------------------------------------------------------------------------

/** `characterTransforms` key: `${setId}:${charId}` (V10) or bare `${charId}` (legacy < V10). */
export function remapCharacterTransformKey(key: string, maps: DeepCopyIdMaps): string {
  const sep = key.indexOf(':')
  if (sep === -1) return lookup(maps.charIdMap, key) // legacy bare character id
  const setId = key.slice(0, sep)
  const charId = key.slice(sep + 1)
  return `${lookup(maps.setIdMap, setId)}:${lookup(maps.charIdMap, charId)}`
}

/** `setSubjectPlacements` key: `${setId}:${subjectId}` (subjectId === character id). */
export function remapSetSubjectKey(key: string, maps: DeepCopyIdMaps): string {
  const sep = key.indexOf(':')
  if (sep === -1) return key
  const setId = key.slice(0, sep)
  const subjectId = key.slice(sep + 1)
  return `${lookup(maps.setIdMap, setId)}:${lookup(maps.charIdMap, subjectId)}`
}

/** `setCameraStates` key: `${setId}:${userId}:${mode}` — only the setId segment is an id. */
export function remapCameraStateKey(key: string, maps: DeepCopyIdMaps): string {
  const sep = key.indexOf(':')
  if (sep === -1) return key
  const setId = key.slice(0, sep)
  const rest = key.slice(sep + 1) // `${userId}:${mode}`
  return `${lookup(maps.setIdMap, setId)}:${rest}`
}

/** `canvasPositions` keys are prefixed by node kind: `asset-{id}`, `subject-{id}`, etc. */
export function remapCanvasPositionKey(key: string, maps: DeepCopyIdMaps): string {
  if (key.startsWith('asset-')) return `asset-${lookup(maps.assetIdMap, key.slice('asset-'.length))}`
  if (key.startsWith('subject-')) return `subject-${lookup(maps.charIdMap, key.slice('subject-'.length))}`
  return key
}

/** `graphicVariableOverrides` key: `${assetId}/${variableId}` — only the asset segment is an id. */
export function remapGraphicVariableOverrideKey(key: string, maps: DeepCopyIdMaps): string {
  const sep = key.indexOf('/')
  if (sep === -1) return key
  return graphicVariableOverrideKey(lookup(maps.assetIdMap, key.slice(0, sep)), key.slice(sep + 1))
}

// ---------------------------------------------------------------------------
// Value transforms (return the SAME reference when nothing changed)
// ---------------------------------------------------------------------------

/**
 * Remap character ids nested in a set-screenshot asset's V9/V10 capture metadata
 * (`v9CaptureData.renderCharacterIds[]` and `characterPlacements[].characterId`).
 * These are characters.id-space ids; without this, a copied project keeps the
 * SOURCE's character ids in shot metadata and set-character position restore
 * lands transforms on dead keys. Returns the same reference when unchanged.
 */
function remapV9CaptureData(v: unknown, maps: DeepCopyIdMaps): unknown {
  if (!v || typeof v !== 'object') return v
  const c = v as Record<string, unknown>
  let changed = false
  const out: Record<string, unknown> = { ...c }

  if (Array.isArray(c.renderCharacterIds)) {
    const ids = c.renderCharacterIds.map(id => (typeof id === 'string' ? lookup(maps.charIdMap, id) : id))
    if (ids.some((id, i) => id !== (c.renderCharacterIds as unknown[])[i])) {
      out.renderCharacterIds = ids
      changed = true
    }
  }

  if (Array.isArray(c.characterPlacements)) {
    const placements = c.characterPlacements.map(p => {
      if (!p || typeof p !== 'object') return p
      const placement = p as Record<string, unknown>
      const cid = placement.characterId
      return typeof cid === 'string' && maps.charIdMap[cid] != null
        ? { ...placement, characterId: maps.charIdMap[cid] }
        : p
    })
    if (placements.some((p, i) => p !== (c.characterPlacements as unknown[])[i])) {
      out.characterPlacements = placements
      changed = true
    }
  }

  return changed ? out : v
}

function remapCharacterIsolationUrls(v: unknown, maps: DeepCopyIdMaps): unknown {
  if (!Array.isArray(v)) return v
  const urls = v.map(item => {
    if (!item || typeof item !== 'object') return item
    const entry = item as Record<string, unknown>
    const characterId = entry.characterId
    return typeof characterId === 'string' && maps.charIdMap[characterId] != null
      ? { ...entry, characterId: maps.charIdMap[characterId] }
      : item
  })
  return urls.some((item, i) => item !== v[i]) ? urls : v
}

function remapAssetValue(v: unknown, maps: DeepCopyIdMaps): unknown {
  if (!v || typeof v !== 'object') return v
  const a = v as Record<string, unknown>
  const newId = typeof a.id === 'string' ? maps.assetIdMap[a.id] : undefined
  const newSetId = typeof a.setId === 'string' ? maps.setIdMap[a.setId] : undefined
  const newCapture = remapV9CaptureData(a.v9CaptureData, maps)
  const newIsolationUrls = remapCharacterIsolationUrls(a.v10CharacterIsolationUrls, maps)
  const newEstablishingIsolationUrls = remapCharacterIsolationUrls(a.v10EstablishingCharacterIsolationUrls, maps)
  const graphic = typeof a.id === 'string' ? maps.motionGraphics?.[a.id] : undefined
  if (
    graphic === undefined &&
    newId == null &&
    newSetId == null &&
    newCapture === a.v9CaptureData &&
    newIsolationUrls === a.v10CharacterIsolationUrls &&
    newEstablishingIsolationUrls === a.v10EstablishingCharacterIsolationUrls
  ) {
    return v
  }
  const out = { ...a }
  if (graphic !== undefined) out.motionGraphic = graphic
  if (newId != null) out.id = newId
  if (newSetId != null) out.setId = newSetId
  if (newCapture !== a.v9CaptureData) out.v9CaptureData = newCapture
  if (newIsolationUrls !== a.v10CharacterIsolationUrls) out.v10CharacterIsolationUrls = newIsolationUrls
  if (newEstablishingIsolationUrls !== a.v10EstablishingCharacterIsolationUrls) {
    out.v10EstablishingCharacterIsolationUrls = newEstablishingIsolationUrls
  }
  return out
}

function remapSubjectValue(v: unknown, maps: DeepCopyIdMaps): unknown {
  if (!v || typeof v !== 'object') return v
  const node = v as SubjectCanvasNode
  let changed = false
  const out = { ...node } as SubjectCanvasNode

  const newId = maps.charIdMap[node.id]
  if (newId != null) {
    out.id = newId
    changed = true
  }
  if (node.mainImageAssetId != null && maps.assetIdMap[node.mainImageAssetId] != null) {
    out.mainImageAssetId = maps.assetIdMap[node.mainImageAssetId]
    changed = true
  }
  if (Array.isArray(node.referenceImageAssetIds)) {
    const arr = node.referenceImageAssetIds.map(a => (a != null && maps.assetIdMap[a] != null ? maps.assetIdMap[a] : a))
    if (arr.some((a, i) => a !== node.referenceImageAssetIds![i])) {
      out.referenceImageAssetIds = arr
      changed = true
    }
  }
  if (Array.isArray(node.poses)) {
    const poses = node.poses.map(p =>
      p.sourceSetId != null && maps.setIdMap[p.sourceSetId] != null
        ? { ...p, sourceSetId: maps.setIdMap[p.sourceSetId] }
        : p,
    )
    if (poses.some((p, i) => p !== node.poses![i])) {
      out.poses = poses
      changed = true
    }
  }
  return changed ? out : v
}

function remapPlacementValue(v: unknown, maps: DeepCopyIdMaps): unknown {
  if (!v || typeof v !== 'object') return v
  const p = v as SetSubjectPlacement
  const newSubjectId = maps.charIdMap[p.subjectId]
  return newSubjectId != null ? { ...p, subjectId: newSubjectId } : v
}

function remapCollectionValue(v: unknown, maps: DeepCopyIdMaps): unknown {
  if (!v || typeof v !== 'object') return v
  const c = v as SubjectCollection
  if (!Array.isArray(c.memberOrder)) return v
  const members = c.memberOrder.map(m =>
    maps.charIdMap[m.subjectId] != null ? { ...m, subjectId: maps.charIdMap[m.subjectId] } : m,
  )
  return members.some((m, i) => m !== c.memberOrder[i]) ? { ...c, memberOrder: members } : v
}

function remapClipValue(v: unknown, maps: DeepCopyIdMaps): unknown {
  if (!v || typeof v !== 'object') return v
  const clip = v as Record<string, unknown>
  if (typeof clip.assetId !== 'string') return v
  const newAssetId = maps.assetIdMap[clip.assetId]
  return newAssetId != null ? { ...clip, assetId: newAssetId } : v
}

function remapVariationStackValue(v: unknown, maps: DeepCopyIdMaps): unknown {
  if (!v || typeof v !== 'object') return v
  const stack = v as Record<string, unknown>
  if (!Array.isArray(stack.memberOrder)) return v
  const members = stack.memberOrder.map(member =>
    typeof member === 'string' ? remapCanvasPositionKey(member, maps) : member,
  )
  return members.some((member, i) => member !== (stack.memberOrder as unknown[])[i])
    ? { ...stack, memberOrder: members }
    : v
}

/** An Adjust record names its Graphic: readers match `record.assetId`, not just the key. */
function remapGraphicVariableOverrideValue(v: unknown, maps: DeepCopyIdMaps): unknown {
  if (!v || typeof v !== 'object') return v
  const record = v as GraphicVariableOverrideRecord
  const assetId = maps.assetIdMap[record.assetId]
  return assetId != null ? { ...record, assetId } : v
}

function remapActionValue(v: unknown, maps: DeepCopyIdMaps): unknown {
  if (!v || typeof v !== 'object') return v
  const action = v as Action
  let changed = false
  const inputRefs = action.inputRefs.map(ref => {
    if (ref.type !== 'asset') return ref
    const id = lookup(maps.assetIdMap, ref.id)
    if (id === ref.id) return ref
    changed = true
    return { ...ref, id }
  })
  // The brief embeds the same asset ids as serialized `@[Asset:Label](id)`
  // tags, and the editor re-derives inputRefs from that text on the next
  // edit — an unmapped tag would resurrect the source project's id.
  const instruction = action.instruction.replace(
    /@\[Asset:([^\]]+)\]\(([^)]+)\)/g,
    (tag, label: string, id: string) => {
      const next = lookup(maps.assetIdMap, id)
      if (next === id) return tag
      changed = true
      return `@[Asset:${label}](${next})`
    },
  )
  return changed ? { ...action, inputRefs, instruction } : v
}

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

const identity = (k: string): string => k

/**
 * Rewrite a Y.Map's entries. Collects entries first (snapshot) then applies only
 * changed entries — key changes via delete+set, value-only changes via set.
 */
function rewriteMap(
  ymap: Y.Map<unknown>,
  transformKey: (k: string) => string,
  transformValue: (v: unknown) => unknown = v => v,
): void {
  for (const [k, v] of Array.from(ymap.entries())) {
    const nk = transformKey(k)
    const nv = transformValue(v)
    if (nk === k) {
      if (nv !== v) ymap.set(k, nv)
    } else {
      ymap.delete(k)
      ymap.set(nk, nv)
    }
  }
}

/**
 * Remap every set/character/set-asset id reference in a captured Yjs document
 * to the copy's freshly-minted ids. Mutates `doc` in a single transaction.
 *
 * Call AFTER `Y.applyUpdate(doc, capturedBytes)` and BEFORE pushing the doc to
 * the new project's room.
 */
export function remapProjectIds(doc: Y.Doc, maps: DeepCopyIdMaps): void {
  doc.transact(() => {
    // Maps whose values carry ids.
    rewriteMap(
      doc.getMap('assets'),
      k => lookup(maps.assetIdMap, k),
      v => remapAssetValue(v, maps),
    )
    rewriteMap(
      doc.getMap('elementCanvasNodes'),
      k => lookup(maps.charIdMap, k),
      v => remapSubjectValue(v, maps),
    )
    rewriteMap(
      doc.getMap('setSubjectPlacements'),
      k => remapSetSubjectKey(k, maps),
      v => remapPlacementValue(v, maps),
    )
    rewriteMap(doc.getMap('subjectCollections'), identity, v => remapCollectionValue(v, maps))
    rewriteMap(doc.getMap('clips'), identity, v => remapClipValue(v, maps))
    rewriteMap(doc.getMap('variationStacks'), identity, v => remapVariationStackValue(v, maps))
    rewriteMap(doc.getMap('actions'), identity, v => remapActionValue(v, maps))
    rewriteMap(
      doc.getMap('graphicVariableOverrides'),
      k => remapGraphicVariableOverrideKey(k, maps),
      v => remapGraphicVariableOverrideValue(v, maps),
    )

    // Maps where only the key carries an id.
    rewriteMap(doc.getMap('setNames'), k => lookup(maps.assetIdMap, k)) // value = display-name string
    rewriteMap(doc.getMap('characterTransforms'), k => remapCharacterTransformKey(k, maps))
    rewriteMap(doc.getMap('setCameraStates'), k => remapCameraStateKey(k, maps))
    rewriteMap(doc.getMap('canvasPositions'), k => remapCanvasPositionKey(k, maps))
    rewriteMap(doc.getMap('assetInOutPoints'), k => lookup(maps.assetIdMap, k))
    rewriteMap(doc.getMap('draftSettings'), k => lookup(maps.assetIdMap, k))
    rewriteMap(doc.getMap('takeCustomizations'), k => lookup(maps.assetIdMap, k))

    // Legacy map (no active frontend consumers); remap defensively by key so any
    // stale entries in old documents stay internally consistent.
    rewriteMap(doc.getMap('characterCanvasNodes'), k => lookup(maps.charIdMap, k))
  })
}
